import React, { useState } from 'react';
import {
  FiMic,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiMessageCircle,
  FiUsers,
  FiUser,
  FiPhoneOff,
  FiSettings,
  FiMoreHorizontal
} from 'react-icons/fi';
import './CallControls.css';

// "CC" glyph used for the captions control, matching the customary
// closed-caption label. Accepts a `size` prop so it can be used
// interchangeably with the react-icons components in this file.
// Hidden from assistive tech by default so screen readers announce the
// button's aria-label (e.g. "Turn on captions") instead of the letters
// "C C"; callers may override aria-hidden via props if needed.
const CCIcon = ({ size = 20, ...props }) => (
  <span
    className="cc-icon"
    style={{ fontSize: Math.round(size * 0.7), lineHeight: 1 }}
    aria-hidden="true"
    {...props}
  >
    CC
  </span>
);

const CallControls = ({
  isAudioEnabled,
  isVideoEnabled,
  isCaptionEnabled,
  isChatOpen = false,
  onToggleAudio,
  onToggleVideo,
  onToggleCaption,
  onToggleChat,
  onToggleParticipants,
  onToggleProfile,
  onEndCall,
  onLeaveCall,
  userRole,
  participantCount,
  chatUnreadCount = 0,
  chatButtonRef = null
}) => {
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // Close more options menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreOptions && !event.target.closest('.more-options')) {
        setShowMoreOptions(false);
      }
    };

    if (showMoreOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setShowMoreOptions(false);
        }
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleClickOutside);
    };
  }, [showMoreOptions]);

  const controlButtons = [
    {
      id: 'audio',
      icon: isAudioEnabled ? FiMic : FiMicOff,
      label: isAudioEnabled ? 'Mute' : 'Unmute',
      ariaLabel: isAudioEnabled ? 'Mute microphone' : 'Unmute microphone',
      onClick: onToggleAudio,
      className: `control-button ${isAudioEnabled ? 'enabled' : 'disabled'}`,
      primary: true
    },
    {
      id: 'video',
      icon: isVideoEnabled ? FiVideo : FiVideoOff,
      label: isVideoEnabled ? 'Stop video' : 'Start video',
      ariaLabel: isVideoEnabled ? 'Stop video' : 'Start video',
      onClick: onToggleVideo,
      className: `control-button ${isVideoEnabled ? 'enabled' : 'disabled'}`,
      primary: true
    },
    {
      id: 'caption',
      icon: CCIcon,
      label: isCaptionEnabled ? 'CC On' : 'CC Off',
      ariaLabel: isCaptionEnabled ? 'Turn off captions' : 'Turn on captions',
      onClick: onToggleCaption,
      className: `control-button caption-button ${isCaptionEnabled ? 'enabled caption-active' : 'disabled'}`,
      primary: true
    },
    {
      id: 'chat',
      icon: FiMessageCircle,
      label: 'Chat',
      ariaLabel: chatUnreadCount > 0
        ? `${isChatOpen ? 'Close' : 'Open'} chat (${chatUnreadCount} unread messages)`
        : (isChatOpen ? 'Close chat' : 'Open chat'),
      onClick: onToggleChat,
      className: `control-button ${isChatOpen ? 'enabled' : ''}`,
      badge: chatUnreadCount > 0 ? chatUnreadCount : null,
      primary: false,
      ariaExpanded: isChatOpen,
      ariaControls: 'incall-chat-panel'
    }
  ];

  // Add participants button for recruiters only
  if (userRole === 'recruiter') {
    controlButtons.push({
      id: 'participants',
      icon: FiUsers,
      label: 'Participants',
      ariaLabel: `View participants (${participantCount} in call)`,
      onClick: onToggleParticipants,
      className: 'control-button',
      badge: participantCount,
      primary: false
    });
  }

  // Add profile button for recruiters
  if (userRole === 'recruiter') {
    controlButtons.push({
      id: 'profile',
      icon: FiUser,
      label: 'Profile',
      ariaLabel: 'View job seeker profile',
      onClick: onToggleProfile,
      className: 'control-button',
      primary: false
    });
  }

  const primaryButtons = controlButtons.filter(btn => btn.primary);
  const secondaryButtons = controlButtons.filter(btn => !btn.primary);


  return (
    <div className="call-controls">
      <div className="controls-container">
        {/* Primary Controls (Always Visible) */}
        <div className="primary-controls" role="toolbar" aria-label="Primary call controls">
          {primaryButtons.map(button => {
            const IconComponent = button.icon;
            return (
              <div key={button.id} className="control-wrapper">
                <button
                  className={button.className}
                  onClick={button.onClick}
                  title={button.ariaLabel}
                  aria-label={button.ariaLabel}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      button.onClick();
                    }
                  }}
                >
                  <IconComponent size={20} aria-hidden="true" />
                  {button.badge && (
                    <span className="control-badge" aria-label={`${button.badge} items`}>{button.badge}</span>
                  )}
                </button>
                <span className="control-label">{button.label}</span>
              </div>
            );
          })}
        </div>

        {/* Secondary Controls */}
        <div className="secondary-controls" role="toolbar" aria-label="Secondary call controls">
          {secondaryButtons.slice(0, 3).map(button => {
            const IconComponent = button.icon;
            return (
              <div key={button.id} className="control-wrapper">
                <button
                  id={button.id === 'chat' ? 'video-call-chat-button' : undefined}
                  ref={button.id === 'chat' ? chatButtonRef : undefined}
                  className={button.className}
                  onClick={button.onClick}
                  title={button.ariaLabel}
                  aria-label={button.ariaLabel}
                  aria-expanded={button.ariaExpanded}
                  aria-controls={button.ariaControls}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      button.onClick();
                    }
                  }}
                >
                  <IconComponent size={20} aria-hidden="true" />
                  {button.badge && (
                    <span className="control-badge" aria-label={`${button.badge} items`}>{button.badge}</span>
                  )}
                </button>
                <span className="control-label">{button.label}</span>
              </div>
            );
          })}

          {/* More Options */}
          {secondaryButtons.length > 3 && (
            <div className="more-options">
              <button
                className="control-button"
                onClick={() => setShowMoreOptions(!showMoreOptions)}
                title="More options"
                aria-label="More options"
                aria-describedby="more-options-tooltip"
                aria-expanded={showMoreOptions}
                aria-haspopup="menu"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowMoreOptions(!showMoreOptions);
                  }
                }}
              >
                <FiMoreHorizontal size={20} aria-hidden="true" />
                <span id="more-options-tooltip" className="tooltip" role="tooltip" aria-live="polite">More options</span>
              </button>

              {showMoreOptions && (
                <div className="more-options-menu" role="menu" aria-label="Additional call options">
                  {secondaryButtons.slice(3).map(button => {
                    const IconComponent = button.icon;
                    return (
                      <button
                        key={button.id}
                        className="menu-item"
                        onClick={() => {
                          button.onClick();
                          setShowMoreOptions(false);
                        }}
                        role="menuitem"
                        tabIndex={0}
                        aria-label={button.label}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            button.onClick();
                            setShowMoreOptions(false);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setShowMoreOptions(false);
                          }
                        }}
                      >
                        <IconComponent size={18} aria-hidden="true" />
                        <span>{button.label}</span>
                        {button.badge && (
                          <span className="menu-badge" aria-label={`${button.badge} items`}>{button.badge}</span>
                        )}
                      </button>
                    );
                  })}

                  <div className="menu-divider"></div>

                  <button
                    className="menu-item"
                    role="menuitem"
                    tabIndex={0}
                    aria-label="Call settings"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        // TODO: Implement settings functionality
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowMoreOptions(false);
                      }
                    }}
                  >
                    <FiSettings size={18} aria-hidden="true" />
                    <span>Settings</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* End Call / Leave Call Button */}
        <div className="end-call-controls" role="toolbar" aria-label="End call controls">
          {(() => {
            // Determine if user is recruiter or not
            const isRecruiter = userRole === 'recruiter' || userRole === 'Recruiter';
            const isJobSeeker = userRole === 'jobseeker' || userRole === 'JobSeeker';
            const isInterpreter = userRole === 'interpreter' || userRole === 'Interpreter' || userRole === 'GlobalInterpreter';
            
            const buttonLabel = isRecruiter ? 'End call' : 'Leave call';
            const handleClick = isRecruiter ? onEndCall : onLeaveCall;
            
            return (
              <div className="control-wrapper">
                <button
                  className="end-call-button"
                  onClick={handleClick}
                  title={buttonLabel}
                  aria-label={buttonLabel}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick();
                    }
                  }}
                >
                  <FiPhoneOff size={20} aria-hidden="true" />
                </button>
                <span className="control-label">{buttonLabel}</span>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default CallControls;
